import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import spacing from "PFTheme/tokens/spacing";
import { ComponentPropsWithRef, HTMLAttributes, JSX } from "react";

export type HtmlContainerTag = Extract<
  keyof JSX.IntrinsicElements,
  | "article"
  | "aside"
  | "div"
  | "label"
  | "dl"
  | "footer"
  | "header"
  | "li"
  | "main"
  | "nav"
  | "ol"
  | "section"
  | "span"
  | "ul"
  | "small"
>;

export type FlexProps<T extends HtmlContainerTag = "div"> = React.AriaAttributes &
  HTMLAttributes<T> &
  React.PropsWithChildren<{
    as?: HtmlContainerTag;
    center?: boolean;
    alignItems?: "center" | "end" | "start" | "stretch" | "baseline";
    justifyContent?:
      | "center"
      | "end"
      | "start"
      | "space-around"
      | "space-between"
      | "space-evenly"
      | "stretch";
    gap?: keyof typeof spacing | null;
    columnGap?: keyof typeof spacing | null;
    rowGap?: keyof typeof spacing | null;
    direction?: "row" | "column" | "row-reverse" | "column-reverse";
    wrap?: "nowrap" | "wrap" | "wrap-reverse";
    grow?: number;
    className?: string;
    inline?: boolean;
    style?: React.CSSProperties;
    qaId?: string;
  }> &
  ComponentPropsWithRef<T>;

export const Flex = <T extends HtmlContainerTag = "div">({
  className,
  style,
  as: Component = "div",
  role,
  center,
  alignItems,
  justifyContent,
  gap = "spacingMd",
  columnGap,
  rowGap,
  children,
  direction,
  wrap,
  grow,
  inline,
  qaId,
  ...props
}: FlexProps<T>) => {
  const flexStyles = omitBy(
    {
      display: inline ? "inline-flex" : "flex",
      alignItems: center ? "center" : alignItems,
      justifyContent: center ? "center" : justifyContent,
      gap: gap && spacing[gap],
      columnGap: columnGap && spacing[columnGap],
      rowGap: rowGap && spacing[rowGap],
      flexDirection: direction,
      flexWrap: wrap,
      flexGrow: grow,
      ...style
    },
    isNil
  );

  return (
    <Component className={className} style={flexStyles} role={role} data-qa-id={qaId} {...(props as any)}>
      {children}
    </Component>
  );
};
